import { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import { Layout, Code2, Link2, Copy, Eye, ExternalLink, BookOpen } from 'lucide-react';

const PRODUCTION_BASE = 'https://okreserva.okgo.mx';

function detectPreviewBase() {
  if (typeof window === 'undefined') return PRODUCTION_BASE;
  return window.location.origin;
}

const WIDGET_DEFAULTS = {
  reservation: {
    publicPath: 'widget/:restaurantId',
    embedTitle: 'Reserva tu mesa',
    iframeSize: { width: 460, height: 760 },
  },
  carta: {
    publicPath: 'widget/carta/:restaurantId',
    embedTitle: 'Carta Express',
    iframeSize: { width: 720, height: 760 },
  },
  experiences: {
    publicPath: 'widget/experiences/:restaurantId',
    embedTitle: 'Experiencias',
    iframeSize: { width: 720, height: 700 },
  },
  events: {
    publicPath: 'widget/events/:restaurantId',
    embedTitle: 'Eventos',
    iframeSize: { width: 720, height: 700 },
  },
};

function buildUrl(base, type, restaurantId, params) {
  const def = WIDGET_DEFAULTS[type] || WIDGET_DEFAULTS.reservation;
  const path = def.publicPath.replace(':restaurantId', restaurantId);
  const sp = new URLSearchParams(params || {});
  const qs = sp.toString();
  return `${base}/${path}${qs ? `?${qs}` : ''}`;
}

function buildContainerMarkup(type, buttonStyle) {
  if (type !== 'reservation') return '';
  if (buttonStyle === 'inline')   return '<div id="okreserva-widget-inline" style="min-height:600px;"></div>';
  if (buttonStyle === 'modal')    return '<div id="okreserva-widget-button"></div>';
  return '<!-- modo floating: el botón se inyecta automáticamente, no se requiere contenedor -->';
}

function buildJsSnippet({ type, restaurantId, productionBase, sdkConfig, containerMarkup }) {
  const sdkUrl = `${productionBase}/widget.js`;
  if (type === 'reservation') {
    return `<!-- OkReserva — Widget de Reservaciones (JavaScript SDK) -->
${containerMarkup}
<script>
  window.OkReservaConfig = ${JSON.stringify({
    restaurantId,
    apiUrl: `${productionBase}/api`,
    ...sdkConfig,
  }, null, 2)};
</script>
<script src="${sdkUrl}" async defer></script>

<!-- API programática disponible tras cargar widget.js:
       window.OkReservaWidget.open();   // abre el widget en modal/floating
       window.OkReservaWidget.close();  // cierra el widget

     Ejemplo: abrir el widget desde tu propio botón
     <button onclick="window.OkReservaWidget.open()">Reservar mesa</button>
-->`;
  }
  // Carta / Experiencias / Eventos: contenedor con data-attrs
  const containerId = type === 'carta' ? 'okreserva-carta'
                     : type === 'experiences' ? 'okreserva-experiences'
                     : 'okreserva-events';
  return `<!-- OkReserva — Widget ${type} (JavaScript SDK) -->
<div id="${containerId}" data-restaurant="${restaurantId}"></div>
<script>
  window.OkReservaConfig = ${JSON.stringify({
    restaurantId,
    type,
    apiUrl: `${productionBase}/api`,
    container: `#${containerId}`,
    ...sdkConfig,
  }, null, 2)};
</script>
<script src="${sdkUrl}" async defer></script>`;
}

export default function WidgetIntegrationPanel({
  type = 'reservation',
  restaurantId,
  urlParams = {},        // params for the public widget URL (lang, color, theme, etc)
  sdkConfig = {},        // extra config injected into window.OkReservaConfig (JS SDK)
  buttonStyle = 'modal', // for reservation widget: modal | floating | inline
  borderRadius = '12px',
  productionBase = PRODUCTION_BASE,
  embedTitle,
  defaultMode = 'iframe',
}) {
  const def = WIDGET_DEFAULTS[type] || WIDGET_DEFAULTS.reservation;
  const previewBase = useMemo(() => detectPreviewBase(), []);
  const [mode, setMode] = useState(defaultMode);
  const [iframeSize, setIframeSize] = useState(def.iframeSize);

  const publicUrl  = useMemo(() => buildUrl(productionBase, type, restaurantId, urlParams),
                              [productionBase, type, restaurantId, urlParams]);
  const previewUrl = useMemo(() => buildUrl(previewBase, type, restaurantId, urlParams),
                              [previewBase, type, restaurantId, urlParams]);

  const containerMarkup = useMemo(() => buildContainerMarkup(type, buttonStyle), [type, buttonStyle]);
  const title = embedTitle || def.embedTitle;

  const iframeCode = `<!-- OkReserva — ${title} (iframe) -->
<iframe
  src="${publicUrl}"
  width="${iframeSize.width}"
  height="${iframeSize.height}"
  style="border:0; max-width:100%; border-radius:${borderRadius}; box-shadow:0 8px 24px rgba(0,0,0,0.08);"
  loading="lazy"
  allow="payment"
  title="${title}">
</iframe>`;

  const jsCode = useMemo(() => buildJsSnippet({
    type, restaurantId, productionBase, sdkConfig, containerMarkup,
  }), [type, restaurantId, productionBase, sdkConfig, containerMarkup]);

  const copyText = (text, label = 'Código') => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copiado al portapapeles`);
  };

  return (
    <div className="space-y-5">
      {/* Sub-tabs iframe / JavaScript / URL */}
      <div className="flex gap-2 p-1 bg-muted rounded-xl w-fit">
        {[
          { k: 'iframe', l: 'iframe',          icon: Layout },
          { k: 'js',     l: 'JavaScript SDK',  icon: Code2 },
          { k: 'url',    l: 'URL Directa',     icon: Link2 },
        ].map(t => (
          <button key={t.k} onClick={() => setMode(t.k)}
            className={cn(
              "flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all",
              mode === t.k ? "bg-white shadow-sm text-orange-600" : "text-muted-foreground hover:text-foreground"
            )}>
            <t.icon className="h-4 w-4" /> {t.l}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Columna izquierda: código generado */}
        <div className="space-y-5">
          {mode === 'iframe' && (
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Layout className="h-5 w-5 text-orange-500" /> Embed vía iframe
                </CardTitle>
                <CardDescription>La forma más sencilla — funciona en cualquier sitio web, plataforma o CMS</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Ancho (px)</Label>
                    <Input type="number" min={300} max={1200} value={iframeSize.width}
                      onChange={e => setIframeSize(s => ({ ...s, width: +e.target.value || 460 }))}
                      className="h-9 text-sm" />
                  </div>
                  <div>
                    <Label className="text-xs">Alto (px)</Label>
                    <Input type="number" min={400} max={1400} value={iframeSize.height}
                      onChange={e => setIframeSize(s => ({ ...s, height: +e.target.value || 760 }))}
                      className="h-9 text-sm" />
                  </div>
                </div>
                <div className="relative">
                  <pre className="p-4 bg-neutral-900 text-neutral-100 rounded-xl text-xs overflow-x-auto max-h-[260px]">
                    <code>{iframeCode}</code>
                  </pre>
                  <Button size="sm" className="absolute top-2 right-2 rounded-lg" onClick={() => copyText(iframeCode)}>
                    <Copy className="h-4 w-4 mr-1" /> Copiar
                  </Button>
                </div>
                <div className="flex gap-2 flex-wrap">
                  {[{l:'Móvil',w:380,h:720},{l:'Tablet',w:560,h:760},{l:'Desktop',w:720,h:780}].map(p => (
                    <button key={p.l} onClick={() => setIframeSize({width:p.w, height:p.h})}
                      className="px-3 py-1.5 text-xs rounded-lg border hover:bg-muted">{p.l}</button>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {mode === 'js' && (
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Code2 className="h-5 w-5 text-orange-500" /> JavaScript SDK avanzado
                </CardTitle>
                <CardDescription>Control total: callbacks, configuración dinámica, tracking</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="relative">
                  <pre className="p-4 bg-neutral-900 text-neutral-100 rounded-xl text-xs overflow-x-auto max-h-[480px]">
                    <code>{jsCode}</code>
                  </pre>
                  <Button size="sm" className="absolute top-2 right-2 rounded-lg" onClick={() => copyText(jsCode)}>
                    <Copy className="h-4 w-4 mr-1" /> Copiar
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {mode === 'url' && (
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Link2 className="h-5 w-5 text-orange-500" /> URL pública directa
                </CardTitle>
                <CardDescription>Compártela por WhatsApp, redes sociales, email o QR</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="relative">
                  <pre className="p-4 bg-neutral-900 text-neutral-100 rounded-xl text-xs overflow-x-auto break-all whitespace-pre-wrap">
                    <code>{publicUrl}</code>
                  </pre>
                  <Button size="sm" className="absolute top-2 right-2 rounded-lg" onClick={() => copyText(publicUrl, 'URL')}>
                    <Copy className="h-4 w-4 mr-1" /> Copiar
                  </Button>
                </div>
                <Button variant="outline" className="w-full rounded-xl" onClick={() => window.open(publicUrl, '_blank')}>
                  <ExternalLink className="h-4 w-4 mr-2" /> Abrir en nueva pestaña
                </Button>
                <div className="flex justify-center pt-2">
                  <img
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(publicUrl)}`}
                    alt="QR del widget"
                    className="rounded-xl border" />
                </div>
              </CardContent>
            </Card>
          )}

          {/* Plataformas + ayuda (siempre visibles) */}
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <BookOpen className="h-4 w-4 text-orange-500" /> Cómo integrarlo
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {[
                { name: 'WordPress', desc: 'Pega el código en un bloque "HTML personalizado" o usa el plugin "Insert Headers and Footers".' },
                { name: 'Wix / Squarespace', desc: 'Añadir → Embed → HTML personalizado y pega el código.' },
                { name: 'Shopify', desc: 'Online Store → Themes → Edit code → pega antes de </body> en theme.liquid.' },
                { name: 'Webflow', desc: 'Añade un Embed element y pega el código.' },
                { name: 'HTML estático', desc: 'Pega directamente en cualquier .html donde quieras mostrar el widget.' },
              ].map(p => (
                <div key={p.name} className="p-3 rounded-xl border text-sm">
                  <p className="font-semibold text-foreground">{p.name}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{p.desc}</p>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>

        {/* Columna derecha: PREVIEW EN VIVO */}
        <div className="space-y-5">
          <Card className="border-0 shadow-lg sticky top-4">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Eye className="h-5 w-5 text-orange-500" /> Vista previa en vivo
              </CardTitle>
              <CardDescription>Esto es exactamente lo que verán tus clientes al embeber el widget</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="rounded-xl bg-slate-100 p-4 flex justify-center overflow-hidden">
                <iframe
                  key={previewUrl + iframeSize.width + iframeSize.height}
                  src={previewUrl}
                  width={Math.min(iframeSize.width, 520)}
                  height={Math.min(iframeSize.height, 720)}
                  style={{ border: 0, borderRadius, background:'#fff', boxShadow:'0 8px 24px rgba(0,0,0,0.08)' }}
                  loading="lazy"
                  title={`Vista previa — ${title}`}
                />
              </div>
              <div className="mt-3 text-xs text-muted-foreground text-center">
                URL canónica: <code className="text-orange-600 break-all">{publicUrl}</code>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
